import React, { useEffect } from 'react';
import styles from './SideBarMenu.module.scss';
import { NavLink } from 'react-router-dom';
import { useAuthSelector } from '../../features/auth/authSlice';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../features/featureFlags/FeatureFlag';
import { useAppStateBillMaintenancePeriodSelector } from '../../features/systemAlerts/billMaintenancePeriodsSlice';

const SidebarItem = ({
    to,
    iconClassName,
    text,
}: {
    to: string;
    iconClassName: string;
    text: string;
}) => (
    <NavLink to={to} activeClassName={styles.active}>
        <div className={styles.item}>
            <div className={`${styles.sideBarMenuLinkTextContainer}`}>
                <i className={iconClassName} />
                {text}
            </div>
        </div>
    </NavLink>
);

export function setSidebarTopHeight(height?: number) {
    // get the height of the system alert box
    const billingMaintenanceHeader = document.querySelector(
        '.MaintenanceBanner_billPayWarningOuterContainer__XE74p',
    ) as HTMLElement;
    const sideBarMenu = document.querySelector(
        '.SideBarMenu_sideBarMenu__wKrLW',
    ) as HTMLElement;
    const header = document.querySelector(
        '.Header_headerWeb__3tcel Header_menu__1bgN5',
    ) as HTMLElement;

    if (billingMaintenanceHeader) {
        // set the height of the 'sideBarMenu' class object
        const alertHeight =
            billingMaintenanceHeader.offsetHeight +
            (header ? header.offsetHeight : 97) +
            10;
        if (sideBarMenu) {
            if (height !== undefined) {
                sideBarMenu.style.top = `${height}px`;
            } else {
                sideBarMenu.style.top = `${alertHeight}px`;
            }
        }
    }
}

export const SideBarMenu: React.FunctionComponent = () => {
    const { isAdmin, authUser } = useAuthSelector((state) => state);
    const { t } = useTranslation();
    const waterBillPaymentRecordsFF = useFeatureFlag(
        'EnableWaterBillHistoryPage',
    );
    const boardsAndCommissionsFF = useFeatureFlag('EnableBoardsAndCommissions');
    const formsBrowserFF = useFeatureFlag('EnableFormsBrowser');

    const { displayUserBillMaintenanceRecords } =
        useAppStateBillMaintenancePeriodSelector((state) => state);

    useEffect(() => {
        if (displayUserBillMaintenanceRecords) {
            setSidebarTopHeight();
        }
    }, [displayUserBillMaintenanceRecords]);

    return (
        <nav className={styles.sideBarMenu} aria-label="Sidebar navigation">
            <h2>{t('SideBar.Menu')}</h2>
            <div className={styles.menu}>
                <SidebarItem
                    to="/dashboard"
                    iconClassName="far fa-home"
                    text={t('SideBar.Home')}
                />
                <SidebarItem
                    to="/in_progress"
                    iconClassName="far fa-spinner"
                    text={t('SideBar.InProgress')}
                />
                <SidebarItem
                    to="/completed_activities"
                    iconClassName="far fa-check-circle"
                    text={t('SideBar.CompletedActivity', { count: 2 })}
                />
                {waterBillPaymentRecordsFF?.enabled && (
                    <SidebarItem
                        to="/payment/water-bill-payment-records"
                        iconClassName="far fa-credit-card"
                        text={t('SideBar.Payment')}
                    />
                )}
                <SidebarItem
                    to={`/account/${authUser?.Id}`}
                    iconClassName="far fa-user-circle"
                    text={t('SideBar.MyProfile')}
                />
                {boardsAndCommissionsFF?.enabled && (
                    <SidebarItem
                        to={`/boards-and-commissions`}
                        iconClassName="far fa-users"
                        text={t('SideBar.BoardsAndCommissions')}
                    />
                )}
                {formsBrowserFF?.enabled && (
                    <SidebarItem
                        to={`/forms-browser`}
                        iconClassName="fa fa-list-alt"
                        text={t('SideBar.FormsBrowser')}
                    />
                )}
            </div>

            {isAdmin ? (
                <div className={`${styles.admin}`}>
                    <h2>{t('SideBar.Administration')}</h2>
                    <div className={styles.menu}>
                        <SidebarItem
                            to="/activities"
                            iconClassName="far fa-file-alt"
                            text={t('SideBar.Activity', { count: 2 })}
                        />
                        <SidebarItem
                            to="/languages"
                            iconClassName="far fa-language"
                            text={t('SideBar.Language', { count: 2 })}
                        />
                        <SidebarItem
                            to="/users"
                            iconClassName="far fa-users-cog"
                            text={t('SideBar.User', { count: 2 })}
                        />
                        <SidebarItem
                            to="/saleforce_public_fields"
                            iconClassName="far fa-clipboard-check"
                            text={t('SideBar.SalesforcePublicField', {
                                count: 2,
                            })}
                        />
                        <SidebarItem
                            to="/system_alerts"
                            iconClassName="far fa-bell"
                            text={t('SideBar.SystemAlerts', { count: 2 })}
                        />
                        <SidebarItem
                            to="/bulk_push_notifications"
                            iconClassName="far fa-envelope"
                            text={'Bulk Push Notifications'}
                        />
                        <SidebarItem
                            to="/delinquency_notifications"
                            iconClassName="far fa-alarm-clock"
                            text={'Delinquencies'}
                        />
                        <SidebarItem
                            to="/billing_maintenance_settings"
                            iconClassName="fa fa-calendar"
                            text={t('Alert.MaintenanceSettings')}
                        />
                        <SidebarItem
                            to="/admin/content"
                            iconClassName="far fa-newspaper"
                            text="Content Management"
                        />
                    </div>
                </div>
            ) : null}
        </nav>
    );
};
